import React, { useState, useEffect } from "react";
import { PageHeader } from "components/ui";
import { Grid, Box } from "@mui/material";
import UploadEvidenceComponent from "components/customizedStandardsComponent/UploadEvidenceComponent";
import DownloadReport from "components/customizedStandardsComponent/DownloadReport";
import DownloadPostReviewGapForm from "components/customizedStandardsComponent/DownloadPostReviewGapForm";
import UploadPostReviewGapForm from "components/customizedStandardsComponent/UploadPostReviewGapForm";
import DownloadPostReviewReports from "components/customizedStandardsComponent/DownloadPostReviewReports";
import NavigationButtons from "helper/navigation/NavigationButtons";
import ReusableFormComponent from "components/customizedStandardsComponent/EsgFormComponent";
import { ESG_BOND_COMPLIANCE_SUBMISSION_FORM } from "data/forms/griFormSchema";
import {
  emissionsTracking,
  emissionsMethodologyChecklist,
  realTimeEmissionCalculation,
  financialInstrumentType,
  eligibilityAndAlignment,
  DisclosureESGIntegration,
  generateReportsEsgBond,
  proceedLinkageCriteria,
} from "data/reports/relevantReports";
import ReportsReviewDialog from "components/customizedStandardsComponent/ReportsReviewDialog";
import UploadEvidenceReviewReports from "components/customizedStandardsComponent/UploadEvidenceReviewReports";
import { APPLICABLE_STANDARD } from "data/standards/griStandards";
import StandardsSelector from "components/customizedStandardsComponent/GriStandardsSelector";

const EsgBondCompliance = () => {
  const [activeStep, setActiveStep] = useState(0);
  const [prevStep, setPrevStep] = useState(0);
  const [reportData, setReportData] = useState({
    selectedStandards: [],
    companyInfo: {},
  });
  const [showReviewPopup, setShowReviewPopup] = useState(false);
  const [forceUpdate, setForceUpdate] = useState(0);

  const steps = [
    "Setup Instrument & Tracking",
    "Choose ESG Standard",
    "Review Eligibility & Alignment",
    "Upload ESG Integration Evidence",
    "Fill Compliance Disclosure",
    "Upload Review Evidence",
    "Generate ESG Bond Reports",
    "Download Gap Assessment Form",
    "Upload Review Evidence",
    "Submit Completed Gap Form",
    "View Final ESG Reports",
    "Download Complete Reports",
  ];

  useEffect(() => {
    if (activeStep === 8 && prevStep === 7) {
      setShowReviewPopup(true);
    }
    setForceUpdate((prev) => prev + 1);
  }, [activeStep, prevStep]);

  const handleNext = () => {
    setPrevStep(activeStep);
    setActiveStep((prevActiveStep) => prevActiveStep + 1);
  };

  const handleBack = () => {
    setPrevStep(activeStep);
    setActiveStep((prevActiveStep) => prevActiveStep - 1);
  };

  const handleSaveStandards = (selectedStandards) => {
    setReportData((prev) => ({
      ...prev,
      selectedStandards,
    }));
    console.log("Standards saved:", selectedStandards);
  };

  const handleClosePopup = () => {
    setShowReviewPopup(false);
  };

  const handleSkipToStep5 = () => {
    setPrevStep(activeStep);
    setActiveStep(5);
    setShowReviewPopup(false);
  };

  const DEFAULT_EVIDENCE_TYPES = [
    "Framework Alignment Matrix",
    "Use of Proceeds Breakdown",
    "SLB KPI Matrix",
    "Second-Party Opinion (SPO) or External Review",
    "ESG Disclosure Report or Financial Prospectus",
    "Use of Proceeds Document",
    "Impact Forecast / Baseline KPIs",
    "Energy/Water/Waste Audit",
    "SDG Impact Statements",
    "Community Benefit Detail",
  ];

  const getStepContent = (step) => {
    switch (step) {
      case 0:
        return (
          <DownloadReport
            key="emissions-tracking-step"
            onPrevious={handleBack}
            onNext={handleNext}
            initialItems={financialInstrumentType}
            ReportDownloadButtonShow={false}
            title=""
            description="Download and review emissions tracking guidelines and frameworks"
            stepId="step-0"
          />
        );

      case 1:
        return (
          <StandardsSelector
            standards={APPLICABLE_STANDARD}
            onNext={handleNext}
            onSave={handleSaveStandards}
            title="Select Applicable Standard"
            nextButtonText="Next"
            initialSelectedStandards={reportData.selectedStandards.map(
              (s) => s.id
            )}
          />
        );
      case 2:
        return (
          <DownloadReport
            key="emissions-tracking-step"
            onPrevious={handleBack}
            onNext={handleNext}
            initialItems={proceedLinkageCriteria}
            title=""
            ReportDownloadButtonShow={false}
            description="Download and review emissions tracking guidelines and frameworks"
            stepId="step-0"
          />
        );

      case 3:
        return (
          <DownloadReport
            key="emissions-tracking-step"
            onPrevious={handleBack}
            onNext={handleNext}
            ReportDownloadButtonShow={false}
            initialItems={eligibilityAndAlignment}
            title=""
            description="Download and review emissions tracking guidelines and frameworks"
            stepId="step-0"
          />
        );
      case 4:
        return (
          <DownloadReport
            key="emissions-tracking-step"
            onPrevious={handleBack}
            ReportDownloadButtonShow={false}
            onNext={handleNext}
            initialItems={DisclosureESGIntegration}
            title=""
            description="Download and review emissions tracking guidelines and frameworks"
            stepId="step-0"
          />
        );

      case 5:
        return (
          <ReusableFormComponent
            {...ESG_BOND_COMPLIANCE_SUBMISSION_FORM}
            onNext={handleNext}
            onPrevious={handleBack}
            showPreviousButton={true}
          />
        );
      case 6:
        return (
          <UploadEvidenceComponent
            onPrevious={handleBack}
            onNext={handleNext}
            evidenceTypes={DEFAULT_EVIDENCE_TYPES}
          />
        );
      case 7:
        return (
          <DownloadReport
            key="realtime-emissionsCalculation-step"
            onPrevious={handleBack}
            onNext={handleNext}
            initialItems={generateReportsEsgBond}
            showReportCountdownButton={true}
            description="Download and review emissions tracking guidelines and frameworks"
            stepId="step-0"
          />
        );
      case 8:
        return (
          <UploadEvidenceReviewReports
            onPrevious={handleBack}
            onNext={handleNext}
          />
        );
      case 9:
        return (
          <DownloadPostReviewGapForm
            onPrevious={handleBack}
            onNext={handleNext}
            title="Gap Assessment Form"
            description="Download gap assessment form to identify missing data or improvement areas"
          />
        );
      case 10:
        return (
          <UploadPostReviewGapForm
            onPrevious={handleBack}
            onNext={handleNext}
            title="Upload Completed Gap Assessment"
            description="Upload your completed gap assessment for final analysis"
          />
        );
      case 11:
        return (
          <DownloadPostReviewReports
            onPrevious={handleBack}
            title="Final GHG Reports"
            standardName="relevant Bond"
            description="Download your complete GHG emissions reports and regulatory submissions"
            showReportCountdownButton={true}
          />
        );
      default:
        return "Unknown step";
    }
  };

  return (
    <>
      <Grid container spacing={2}>
        <Grid item xs={12}>
          <Box sx={{ display: "flex", alignItems: "center", mb: 2 }}>
            <PageHeader
              title="AI-Powered ESG Reporting across Global, National, Sectoral, and Industry-Specific Standards"
              subtitle="AI-Powered greenhouse gas emissions tracking, calculation, and regulatory reporting"
            />
          </Box>
          <Grid item xs={12}>
            <Box sx={{ display: "flex", alignItems: "center", mb: 2 }}>
              <NavigationButtons
                imageKey="dashboardImg2_3"
                title="Sustainable Finance & Bond Standards"
                activeStep={activeStep}
                onBack={handleBack}
                steps={steps}
              />
            </Box>
          </Grid>
        </Grid>

        <Grid item xs={12}>
          <Box key={`step-content-${activeStep}-${forceUpdate}`}>
            {getStepContent(activeStep)}
          </Box>
        </Grid>

        <Grid item xs={12}>
          <Box
            sx={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              mt: 4,
              mb: 2,
            }}
          >
            {/* <Box sx={{ textAlign: 'center' }}>
                            <Box sx={{ mb: 1, fontSize: '0.875rem', color: 'text.secondary' }}>
                                Step {activeStep + 1} of {steps.length}: {steps[activeStep]}
                            </Box>
                        </Box> */}
          </Box>
          <Box
            sx={{
              display: "flex",
              justifyContent: "center",
              mt: -1,
            }}
          >
            {steps.map((_, index) => (
              <Box
                key={index}
                sx={{
                  width: 10,
                  height: 10,
                  borderRadius: "50%",
                  backgroundColor: index <= activeStep ? "#02462E" : "#e0e0e0",
                  mx: 1,
                  transition: "background-color 0.3s ease",
                }}
              />
            ))}
          </Box>
        </Grid>
      </Grid>

      <ReportsReviewDialog
        open={showReviewPopup}
        onClose={handleClosePopup}
        onSkip={handleSkipToStep5}
      />
    </>
  );
};

export default EsgBondCompliance;
